import React from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import uniqWith from 'lodash/uniqWith';
import Lozenge from '@atlaskit/lozenge';
import { Text } from '@atlaskit/primitives';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage, useIntl, type IntlShape } from '@atlassian/jira-intl';
import { StatusLozenge as IssueStatusFieldReadonly } from '@atlassian/jira-issue-field-status/src/ui/status-lozenge/index.tsx';
import { useDateFormatter } from '@atlassian/jira-portfolio-3-common/src/date-manipulation/format.tsx';
import { WarningTypes } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/command/warnings/engine';
import { NO_LABEL_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/label-filter';
import { NO_RELEASE_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/release-filter';
import { UNSAVED_STATUS } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/status-key-filter/pure/index.tsx';
import { NO_TEAM_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/team-filter';
import {
	ASSIGNEE_FILTER_ID,
	REPORTER_FILTER_ID,
	DATE_RANGE_FILTER_ID,
	ISSUE_WARNING_FILTER_ID,
	type DateRangeFilterValue,
	LABEL_FILTER_ID,
	PROJECT_FILTER_ID,
	RELEASE_FILTER_ID,
	STATUS_KEY_FILTER_ID,
	SUMMARY_FILTER_ID,
	TEAM_FILTER_ID,
	WARNING_FILTER_ID,
	COMPONENT_FILTER_ID,
	DEPENDENCIES_FILTER_ID,
	GOAL_FILTER_ID,
	ISSUE_PRIORITIES_FILTER_ID,
	ISSUE_TYPE_KEY_FILTER_ID,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/view-settings/filters/types';
import dependenciesFilterMessages from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/filter-bar/dependencies-filter/messages';
import labelFilterMessages from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/filter-bar/label-filter/messages';
import filterMessages from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/filter-bar/messages';
import releaseFilterMessages from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/filter-bar/release-filter/messages';
import statusFilterMessages from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/filter-bar/status-key-filter/messages';
import teamFilterMessages from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/filter-bar/team-filter/messages';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import CustomFieldsFilterGroup from './custom-fields';
import FilterGroup from './filter-group/view';
import messages from './messages';
import SprintFilterGroup from './sprint-filter-group/view';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types';

const formatDateRange = (
	intl: IntlShape,
	formatTimestamp: (timestamp: number) => string,
	dateRange: DateRangeFilterValue,
) => {
	const { startDate, endDate } = dateRange;
	if (startDate && endDate) {
		return intl.formatMessage(messages.dateRange, {
			startDate: formatTimestamp(startDate),
			endDate: formatTimestamp(endDate),
		});
	}

	if (startDate) {
		return intl.formatMessage(messages.dateRangeFrom, {
			startDate: formatTimestamp(startDate),
		});
	}

	if (endDate) {
		return intl.formatMessage(messages.dateRangeTo, {
			endDate: formatTimestamp(endDate),
		});
	}
	return '';
};

export default function FilterViewInformation({
	releases,
	crossProjectReleases,
	teams,
	summary,
	issueTypeKeys,
	issuePriorities,
	assignees,
	reporters,
	additionalSprints,
	sprintCategories,
	warnings,
	goals,
	labels,
	statusKeys,
	projects,
	components,
	dependencies,
	customFieldFilterCount,
	CustomFieldsFilterGroupComponent = CustomFieldsFilterGroup,
	isAtlasConnectInstalled,
	dateRange,
	issueWarnings,
	definedGoalsByARI,
}: Props) {
	const intl = useIntl();
	const filterGroups = [];
	const { formatTimestamp } = useDateFormatter();

	const exportPNGLabelLozenge = (index: number, name: string) => (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div key={`lozenge-div-${index}`} className={styles.exportPNGLozenge}>
			<Lozenge key={`lozenge-${index}`}>{name}</Lozenge>
		</div>
	);

	if (releases.length > 0) {
		filterGroups.push({
			id: 'releasesFilterGroup',
			node: (
				<FilterGroup
					title={intl.formatMessage(filterMessages[RELEASE_FILTER_ID])}
					truncateMessageKey={messages.remainingReleases}
					items={[
						...releases.map((item) =>
							item.id === NO_RELEASE_ID
								? intl.formatMessage(releaseFilterMessages.noReleasesFilterText)
								: item.name,
						),
						...crossProjectReleases.map((item) => item.name),
					]}
				/>
			),
		});
	}
	if (teams.length > 0) {
		filterGroups.push({
			id: 'teamsFilterGroup',
			node: (
				<FilterGroup
					title={intl.formatMessage(filterMessages[TEAM_FILTER_ID])}
					truncateMessageKey={messages.remainingTeams}
					items={teams.map((item) =>
						item.id === NO_TEAM_ID
							? intl.formatMessage(teamFilterMessages.noTeamFilterText)
							: item.name,
					)}
				/>
			),
		});
	}
	if (summary.length > 0) {
		filterGroups.push({
			id: 'summaryFilterGroup',
			node: (
				<>
					{fg('replace_typography_css_with_tokens_arj') ? (
						<Text as="strong">{intl.formatMessage(filterMessages[SUMMARY_FILTER_ID])}</Text>
					) : (
						// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
						<strong>{intl.formatMessage(filterMessages[SUMMARY_FILTER_ID])} </strong>
					)}

					<span>({summary})</span>
				</>
			),
		});
	}
	if (additionalSprints.length > 0 || sprintCategories.length > 0) {
		filterGroups.push({
			id: 'sprintsFilterGroup',
			node: (
				<SprintFilterGroup
					additionalSprints={additionalSprints}
					sprintCategories={sprintCategories}
				/>
			),
		});
	}

	if (projects.length > 0) {
		filterGroups.push({
			id: 'projectsFilterGroup',
			node: (
				<FilterGroup
					title={intl.formatMessage(filterMessages[PROJECT_FILTER_ID])}
					truncateMessageKey={messages.remainingProjects}
					items={projects.map((item) => (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						<span key={`project-${item.id}`} className={styles.filterItemWithIcon}>
							{isDefined(item.iconUrl) && (
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								<img className={`${styles.icon} ${styles.circle}`} alt="" src={item.iconUrl} />
							)}
							<span>
								{item.name} ({item.key})
							</span>
						</span>
					))}
				/>
			),
		});
	}
	if (assignees.length > 0) {
		filterGroups.push({
			id: 'assigneesFilterGroup',
			node: (
				<FilterGroup
					title={intl.formatMessage(filterMessages[ASSIGNEE_FILTER_ID])}
					truncateMessageKey={messages.remainingAssignees}
					items={assignees.map((item) => item.name)}
				/>
			),
		});
	}
	if (reporters.length > 0) {
		filterGroups.push({
			id: 'reportersFilterGroup',
			node: (
				<FilterGroup
					title={intl.formatMessage(filterMessages[REPORTER_FILTER_ID])}
					truncateMessageKey={messages.remainingReporters}
					items={reporters.map((item) => item.name)}
				/>
			),
		});
	}

	// issueTypeKeys actually issue status objects here
	if (issueTypeKeys.length > 0) {
		filterGroups.push({
			id: 'issueTypesFilterGroup',
			node: (
				<FilterGroup
					title={intl.formatMessage(filterMessages[ISSUE_TYPE_KEY_FILTER_ID])}
					truncateMessageKey={messages.remainingIssueTypes}
					items={uniqWith(
						issueTypeKeys.map(({ key, name, iconUrl }) => ({ key, name, iconUrl })),
						isEqual,
					).map(({ key: level, name, iconUrl }) => (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						<span key={`issueType-${level}-${name}`} className={styles.filterItemWithIcon}>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<img className={styles.icon} alt="" src={iconUrl} />
							{name}
						</span>
					))}
				/>
			),
		});
	}

	if (issuePriorities.length > 0) {
		filterGroups.push({
			id: 'issuePrioritiesFilterGroup',
			node: (
				<FilterGroup
					title={intl.formatMessage(filterMessages[ISSUE_PRIORITIES_FILTER_ID])}
					truncateMessageKey={messages.remainingIssuePriorities}
					items={issuePriorities.map((item) => item.name)}
				/>
			),
		});
	}
	if (components.length > 0) {
		filterGroups.push({
			id: 'componentsFilterGroup',
			node: (
				<FilterGroup
					title={intl.formatMessage(filterMessages[COMPONENT_FILTER_ID])}
					truncateMessageKey={messages.remainingComponents}
					items={components.map((item) => (
						<span key={`components-${item.id}`}>
							{item.name} ({item.key})
						</span>
					))}
				/>
			),
		});
	}
	if (labels.length > 0) {
		filterGroups.push({
			id: 'labelsFilterGroup',
			node: (
				<FilterGroup
					title={intl.formatMessage(filterMessages[LABEL_FILTER_ID])}
					truncateMessageKey={messages.remainingLabels}
					items={labels.map((item, index) =>
						item.id === NO_LABEL_ID
							? intl.formatMessage(labelFilterMessages.noLabelsFilterText)
							: exportPNGLabelLozenge(index, item.name),
					)}
				/>
			),
		});
	}

	if (isAtlasConnectInstalled && goals.length > 0 && !isEmpty(definedGoalsByARI)) {
		filterGroups.push({
			id: 'goalsFilterGroup',
			node: (
				<FilterGroup
					title={intl.formatMessage(filterMessages[GOAL_FILTER_ID])}
					truncateMessageKey={messages.remainingGoals}
					items={goals.map((item) => definedGoalsByARI[item.name].name)}
				/>
			),
		});
	}
	if (dependencies.type !== 'off') {
		if (dependencies.type === 'specificIssue') {
			const issue = (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				<span className={styles.filterItemWithIcon}>
					{isDefined(dependencies.issueTypeIconUrl) && (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						<img className={styles.icon} alt="" src={dependencies.issueTypeIconUrl} />
					)}
					{dependencies.issueKey} {dependencies.issueSummary}
				</span>
			);
			const message =
				dependencies.includeTransitive === true
					? messages.specificIssueTriggerWithDependencyChain
					: dependenciesFilterMessages.specificIssueTrigger;

			filterGroups.push({
				id: 'dependenciesFilterGroup',
				node: (
					<>
						{fg('replace_typography_css_with_tokens_arj') ? (
							<Text as="strong">{intl.formatMessage(filterMessages[DEPENDENCIES_FILTER_ID])}</Text>
						) : (
							// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
							<strong>{intl.formatMessage(filterMessages[DEPENDENCIES_FILTER_ID])} </strong>
						)}
						{fg('replace_typography_css_with_tokens_arj') ? (
							<Text>
								(<FormattedMessage {...message} values={{ issueKey: issue }} />)
							</Text>
						) : (
							// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
							<span>
								(
								<FormattedMessage {...message} values={{ issueKey: issue }} />)
							</span>
						)}
					</>
				),
			});
		} else {
			filterGroups.push({
				id: 'dependenciesFilterGroup',
				node: (
					<>
						{fg('replace_typography_css_with_tokens_arj') ? (
							<Text as="strong">{intl.formatMessage(filterMessages[DEPENDENCIES_FILTER_ID])}</Text>
						) : (
							// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
							<strong>{intl.formatMessage(filterMessages[DEPENDENCIES_FILTER_ID])} </strong>
						)}
						<span>
							({intl.formatMessage(dependenciesFilterMessages[`${dependencies.type}Trigger`])})
						</span>
					</>
				),
			});
		}
	}

	// statusKeys actually status objects here
	if (statusKeys.length > 0) {
		filterGroups.push({
			id: 'statusesFilterGroup',
			node: (
				<FilterGroup
					title={intl.formatMessage(filterMessages[STATUS_KEY_FILTER_ID])}
					truncateMessageKey={messages.remainingStatuses}
					items={uniqWith(
						statusKeys.map(({ categoryId, name }) => ({ categoryId, name })),
						isEqual,
					).map(({ categoryId, name }, index) =>
						name === UNSAVED_STATUS.name ? (
							intl.formatMessage(statusFilterMessages.unsavedIssues)
						) : (
							<IssueStatusFieldReadonly
								key={index}
								value={{
									id: `${index}`,
									name,
									statusCategory: { id: categoryId },
								}}
							/>
						),
					)}
				/>
			),
		});
	}

	if (ff('plans-warning-filter-rework_7pi1k')) {
		if (issueWarnings.length > 0) {
			filterGroups.push({
				id: 'issueWarningsFilterGroup',
				node: (
					<FilterGroup
						title={intl.formatMessage(filterMessages[ISSUE_WARNING_FILTER_ID])}
						truncateMessageKey={messages.remainingWarnings}
						items={issueWarnings
							.map((warningId) => WarningTypes[warningId])
							.filter((warning) => warning !== undefined)
							.map((warning) => intl.formatMessage(warning.header))}
					/>
				),
			});
		}

		if (dateRange) {
			filterGroups.push({
				id: 'dateRangeFilterGroup',
				node: (
					<>
						{fg('replace_typography_css_with_tokens_arj') ? (
							<Text as="strong">{intl.formatMessage(filterMessages[DATE_RANGE_FILTER_ID])}</Text>
						) : (
							// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
							<strong>{intl.formatMessage(filterMessages[DATE_RANGE_FILTER_ID])}</strong>
						)}
						<span>({formatDateRange(intl, formatTimestamp, dateRange)})</span>
					</>
				),
			});
		}
	} else if (warnings) {
		filterGroups.push({
			id: 'warningsFilterGroup',
			node: (
				<>
					{fg('replace_typography_css_with_tokens_arj') ? (
						<Text as="strong">{intl.formatMessage(filterMessages[WARNING_FILTER_ID])}</Text>
					) : (
						// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
						<strong>{intl.formatMessage(filterMessages[WARNING_FILTER_ID])}</strong>
					)}
					<span>({intl.formatMessage(messages.issuesWithWarnings)})</span>
				</>
			),
		});
	}

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div className={styles.filterGroups}>
			{filterGroups.length > 0 &&
				filterGroups.map((item, i) => (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					<span key={item.id} className={styles.filterGroupItem}>
						{item.node}
						{i < filterGroups.length - 1 || customFieldFilterCount > 0 ? ',' : ''}
					</span>
				))}
			{customFieldFilterCount > 0 && <CustomFieldsFilterGroupComponent />}
		</div>
	);
}
